import { Fragment, useId } from "react";
import { MultiSelect } from "primereact/multiselect";

import { GroupedConflict } from "../schedule-conflicts";
import {
  DayPatternEntry,
  InvalidPatternDay,
  WEEKDAY_NAMES,
} from "../day-pattern";

import classes from "./DayPatternFields.module.scss";

export type DayPatternFieldsProps = {
  value: DayPatternEntry[];
  employeeOptions: { label: string; value: string }[];
  onChange: (next: DayPatternEntry[]) => void;
  /**
   * From describeInvalidPatternDays, for the very pattern in `value` - each
   * entry is matched to a card by its `index`, so a list computed from some
   * other array would land its messages on the wrong cards. May hold more than
   * one entry per card.
   */
  invalidDays?: InvalidPatternDay[];
  /**
   * Schedule clashes keyed by weekday (0 = Sunday), each list rendered directly
   * beneath that weekday's card. A weekday with no card is simply not shown
   * here - the caller keeps those lines elsewhere so none is ever hidden.
   */
  conflictsByWeekday?: Record<number, GroupedConflict[]>;
};

/**
 * One card per configured weekday, so a job can run with different hours and a
 * different crew on each of its days.
 *
 * Cards are keyed and updated by array index rather than by dayOfWeek, and
 * messages are matched to them the same way. The weekday chips cannot produce a
 * repeated weekday - syncPatternWithDays dedupes through a Set - but a pattern
 * loaded from the API can, and describeInvalidPatternDays exists to flag exactly
 * that. A dayOfWeek key would collide on such a pattern and send edits, and
 * messages, to the wrong card.
 *
 * Each message is attached to the control it is about with aria-describedby,
 * and that control reports aria-invalid, so a screen reader announces the
 * problem *with* the input rather than leaving the two to be paired up by
 * reading past it. A repeated weekday is nothing any one control can fix, so
 * that message describes the card's group instead and marks no input invalid.
 *
 * Message ids need a per-instance prefix, and useId is what supplies it: the
 * (index, field) pair is unique within one list, but two of these on a page
 * would otherwise emit the same ids and the second one's inputs would resolve
 * to the first one's messages. It is called once for the whole list rather than
 * per card, so the cards stay hook-free and stay plain `.map` output. The
 * controls keep their aria-labels - only the messages need ids.
 *
 * Times are native <input type="time">, which speaks "HH:mm" - exactly what
 * DayPatternEntry holds - so no Date conversion layer sits in between.
 */
export const DayPatternFields = ({
  value,
  employeeOptions,
  onChange,
  invalidDays = [],
  conflictsByWeekday = {},
}: DayPatternFieldsProps) => {
  // Above the early return: a hook skipped on the empty pattern would break the
  // rules of hooks the moment the first day is selected.
  const baseId = useId();

  if (value.length === 0) {
    return null;
  }

  // The whole array goes back on every edit, with the untouched entries kept by
  // reference so a parent can tell what actually changed.
  const updateDay = (index: number, changes: Partial<DayPatternEntry>) =>
    onChange(
      value.map((entry, i) => (i === index ? { ...entry, ...changes } : entry)),
    );

  return (
    <div className={classes.dayPatternFields}>
      {value.map((entry, index) => {
        const dayName = WEEKDAY_NAMES[entry.dayOfWeek];
        // Every problem for this entry, not just the first: a day can be both
        // zero-length and unstaffed, and fixing one would otherwise silently
        // reveal the other.
        const problems = invalidDays.filter(
          (problem) => problem.index === index,
        );
        const problemId = (problem: InvalidPatternDay) =>
          `${baseId}-${problem.index}-${problem.field}`;
        // Undefined rather than "" when a control is sound: an empty
        // aria-describedby is still an attribute, and aria-invalid="false" says
        // something this control has no business saying.
        const describedBy = (field: keyof DayPatternEntry) => {
          const own = problems.filter((problem) => problem.field === field);

          return own.length > 0 ? own.map(problemId).join(" ") : undefined;
        };

        const dayConflicts = conflictsByWeekday[entry.dayOfWeek] ?? [];

        return (
          <Fragment
            // Index, not dayOfWeek: a repeated weekday would collide.
            key={index}
          >
            <div
              className={classes.dayCard}
              role="group"
              aria-label={dayName}
              // A repeated weekday is the card's problem, not any one control's.
              aria-describedby={describedBy("dayOfWeek")}
            >
              <span className={classes.dayName}>{dayName}</span>

              <div className={classes.field}>
                <span className={classes.fieldLabel}>Start</span>
                <input
                  type="time"
                  className={classes.timeInput}
                  // aria-label rather than a <label for> pair. baseId could
                  // build label ids too, now that it exists, but a label carried
                  // on the control needs no id at all - and a repeated weekday
                  // means the weekday name alone could never have served as one.
                  aria-label={`${dayName} start time`}
                  aria-describedby={describedBy("startTime")}
                  aria-invalid={describedBy("startTime") ? true : undefined}
                  value={entry.startTime}
                  onChange={(event) =>
                    updateDay(index, { startTime: event.target.value })
                  }
                />
              </div>

              <div className={classes.field}>
                <span className={classes.fieldLabel}>End</span>
                <input
                  type="time"
                  className={classes.timeInput}
                  aria-label={`${dayName} end time`}
                  aria-describedby={describedBy("endTime")}
                  aria-invalid={describedBy("endTime") ? true : undefined}
                  value={entry.endTime}
                  onChange={(event) =>
                    updateDay(index, { endTime: event.target.value })
                  }
                />
              </div>

              <div className={classes.employees}>
                <span className={classes.fieldLabel}>Employees</span>
                <MultiSelect
                  // No maxSelectedLabels: with display="chip" PrimeReact slices
                  // the chip list to it, which would hide part of the crew this
                  // card exists to show.
                  appendTo="self"
                  display="chip"
                  filter
                  // PrimeReact moves every aria* prop onto the hidden
                  // role="listbox" input that carries the label, so these land on
                  // the same element the user's cursor lands on.
                  aria-label={`${dayName} employees`}
                  aria-describedby={describedBy("employeeIds")}
                  aria-invalid={describedBy("employeeIds") ? true : undefined}
                  options={employeeOptions}
                  placeholder="Select employee"
                  value={entry.employeeIds}
                  onChange={(event) =>
                    updateDay(index, { employeeIds: event.value })
                  }
                  className={classes.employeeSelect}
                />
              </div>

              {problems.length > 0 && (
                <ul className={classes.problems}>
                  {problems.map((problem) => (
                    // Keyed by the same id the control points at: one entry
                    // reports at most one problem per field.
                    <li key={problemId(problem)} id={problemId(problem)}>
                      {problem.reason}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {dayConflicts.length > 0 && (
              <ul className={classes.conflicts}>
                {dayConflicts.map((group) => (
                  <li
                    key={group.key}
                    className={
                      group.severity === "error"
                        ? "p-error"
                        : classes.conflictWarning
                    }
                  >
                    {group.text}
                  </li>
                ))}
              </ul>
            )}
          </Fragment>
        );
      })}
    </div>
  );
};
